import { __rest } from 'tslib';
import baseDiscoverService from './base/baseDiscoverService.js';

const RUNNING_ASSESSMENT_STATUSES = ["generating_queries", "executing_queries", "aggregating"];
var BillingMode;
(function (BillingMode) {
    BillingMode["Enterprise"] = "enterprise";
    BillingMode["Demo"] = "demo";
    BillingMode["SelfServe"] = "self-serve";
})(BillingMode || (BillingMode = {}));
const createReport = (payload, config) => {
    return baseDiscoverService.post("api/reports", payload, config);
};
const createMarketProfileReport = (payload, config) => {
    return baseDiscoverService.post("api/reports/market-profile", payload, config);
};
const continueAssessment = (assessmentId, payload, config) => {
    return baseDiscoverService.post(`api/reports/${assessmentId}/continue`, payload || {}, config);
};
const getAssessmentStatus = (assessmentId, config) => {
    return baseDiscoverService.get(`api/reports/${assessmentId}`, config);
};
const getAiDiscoverabilityReport = (id, config) => {
    return baseDiscoverService.get(`api/reports/${id}`, config);
};
const getAiDiscoverabilityReports = (params, config) => {
    var _a, _b;
    const queryParams = Object.assign(Object.assign({}, (((_a = params === null || params === void 0 ? void 0 : params.ids) === null || _a === void 0 ? void 0 : _a.length) && { ids: params.ids.join(",") })), (((_b = params === null || params === void 0 ? void 0 : params.subjectIds) === null || _b === void 0 ? void 0 : _b.length) && { subjectIds: params.subjectIds.join(",") }));
    return baseDiscoverService.get(`api/reports`, Object.assign(Object.assign({}, config), { params: Object.keys(queryParams).length ? queryParams : undefined }));
};
const getDiscoverSubjectTimeSeries = (subjectId, config) => {
    return baseDiscoverService.get(`api/subjects/${subjectId}/time-series`, config);
};
const getDiscoverSubjectTopSources = (subjectId, config) => {
    return baseDiscoverService.get(`api/subjects/${subjectId}/top-sources`, config);
};
const updateDiscoverOrganization = (organizationId, payload, config) => {
    return baseDiscoverService.put(`api/organizations/${organizationId}`, payload, config);
};
const updateDiscoverSubject = (subjectId, payload, config) => {
    return baseDiscoverService.put(`api/subjects/${subjectId}`, payload, config);
};
const createDiscoverSubject = (payload, config) => {
    return baseDiscoverService.post(`api/subjects`, payload, config);
};
const getDiscoverSubjectById = (subjectId, config) => {
    return baseDiscoverService.get(`api/subjects/${subjectId}`, config);
};
const getDiscoverSubjects = (params, config) => {
    return baseDiscoverService.get(`api/subjects`, Object.assign(Object.assign({}, config), { params }));
};
const getDiscoverOrganizations = (config) => {
    return baseDiscoverService.get(`api/organizations`, config);
};
const createDiscoverOrganization = (payload, config) => {
    return baseDiscoverService.post(`api/organizations`, payload, config);
};
const deleteDiscoverSubject = (subjectId, config) => {
    return baseDiscoverService.delete(`api/subjects/${subjectId}`, config);
};
const deleteDiscoverOrganization = (organizationId, config) => {
    return baseDiscoverService.delete(`api/organizations/${organizationId}`, config);
};
const USERS_SORT_BY = {
    user: "name",
    organization: "organization",
    lastSignIn: "lastSignInAt",
    access: "access",
};
const firstOf = (value) => (Array.isArray(value) ? value[0] : value);
const toGetUsersWireParams = (params) => {
    var _a, _b;
    const source = params !== null && params !== void 0 ? params : {};
    const organizationId = (_a = source.organizationId) !== null && _a !== void 0 ? _a : (_b = source.organizations) === null || _b === void 0 ? void 0 : _b[0];
    const roleFilter = Array.isArray(source.role) && source.role.length !== 1 ? undefined : firstOf(source.role);
    const statusFilter = Array.isArray(source.status) && source.status.length !== 1 ? undefined : firstOf(source.status);
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (source.skip !== undefined ? { skip: source.skip } : {})), (source.limit !== undefined ? { limit: source.limit } : {})), (source.search ? { search: source.search } : {})), (organizationId ? { organizationId } : {})), (source.orderBy ? { sortBy: USERS_SORT_BY[source.orderBy] } : {})), (source.order ? { sortOrder: source.order } : {})), (roleFilter ? { discoverRoleAlias: roleFilter } : {})), (statusFilter ? { status: statusFilter } : {}));
};
// The subject-scope half of ONE membership entry. The backend validates create/update bodies with
// Joi and allows neither unknown nor stripped keys, so every key here is conditional rather than
// emitted-and-ignored:
//   - discover_admin: both `allSubjects` and `subjects` are forbidden — emit nothing.
//   - viewer + allCommunities: `allSubjects: true`, and `subjects` is forbidden alongside it.
//   - viewer, scoped: `allSubjects: false` plus the subject ids under `subjects` (NOT `subjectIds`).
// The empty-list case emits no `subjects` key rather than `subjects: []`: create requires min 1 and
// update forbids an empty array, so `[]` is a guaranteed 400 either way. It should be unreachable —
// `communitiesFieldSchema` in UserCreationFormConfig blocks a scoped viewer with no communities on
// both the flat form and each block of the multi-membership form — this is belt-and-braces.
const toUserAccessWireBody = (role, communityIds, allCommunities) => {
    if (role !== "discover_viewer")
        return {};
    if (allCommunities)
        return { allSubjects: true };
    const subjects = communityIds !== null && communityIds !== void 0 ? communityIds : [];
    return Object.assign({ allSubjects: false }, (subjects.length ? { subjects } : {}));
};
// One app-facing membership becomes one wire entry. `discoverRole` maps to `roleAlias` and the
// community scope expands per the rules above; nothing else crosses.
const toMembershipWireEntry = (membership) => (Object.assign({ organizationId: membership.organizationId, roleAlias: membership.discoverRole }, toUserAccessWireBody(membership.discoverRole, membership.communityIds, membership.allCommunities)));
const toCreateUserWireBody = (payload) => (Object.assign(Object.assign({ name: payload.name, email: payload.email }, (payload.phone !== undefined ? { phone: payload.phone } : {})), { memberships: payload.memberships.map(toMembershipWireEntry) }));
const toUpdateUserWireBody = (payload) => {
    var _a;
    return (Object.assign(Object.assign(Object.assign({}, (payload.name !== undefined ? { name: payload.name } : {})), (payload.phone !== undefined ? { phone: payload.phone } : {})), (((_a = payload.memberships) === null || _a === void 0 ? void 0 : _a.length) ? { memberships: payload.memberships.map(toMembershipWireEntry) } : {})));
};
const fromDiscoverUserWire = (user) => {
    var _a;
    return ({
        _id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        discoverRole: user.discoverRoleAlias,
        status: user.status,
        lastSignInAt: (_a = user.lastSignInAt) !== null && _a !== void 0 ? _a : null,
        organizations: user.organizations,
        communities: user.subjects,
        allCommunities: user.allSubjects,
        accessCount: user.accessCount,
    });
};
const mapUsersListResponse = (body) => {
    var _a, _b;
    const wireUsers = ((_b = (_a = body === null || body === void 0 ? void 0 : body.data) === null || _a === void 0 ? void 0 : _a.users) !== null && _b !== void 0 ? _b : []);
    return Object.assign(Object.assign({}, body), { data: Object.assign(Object.assign({}, body.data), { users: wireUsers.map(fromDiscoverUserWire) }) });
};
const mapUserResponse = (body) => {
    var _a;
    const wireUser = (_a = body === null || body === void 0 ? void 0 : body.data) === null || _a === void 0 ? void 0 : _a.user;
    return wireUser ? Object.assign(Object.assign({}, body), { data: Object.assign(Object.assign({}, body.data), { user: fromDiscoverUserWire(wireUser) }) }) : body;
};
const getDiscoverUsers = (params, config) => {
    return baseDiscoverService
        .get(`api/users`, Object.assign(Object.assign({}, config), { params: toGetUsersWireParams(params) }))
        .then((body) => mapUsersListResponse(body));
};
const createDiscoverUser = (payload, config) => {
    return baseDiscoverService
        .post(`api/users`, toCreateUserWireBody(payload), config)
        .then((body) => mapUserResponse(body));
};
const updateDiscoverUser = (userId, payload, config) => {
    return baseDiscoverService
        .put(`api/users/${userId}`, toUpdateUserWireBody(payload), config)
        .then((body) => mapUserResponse(body));
};
const updateDiscoverUserStatus = (userId, payload, config) => {
    return baseDiscoverService
        .patch(`api/users/${userId}/status`, payload, config)
        .then((body) => mapUserResponse(body));
};
const deleteDiscoverUser = (userId, config) => {
    return baseDiscoverService.delete(`api/users/${userId}`, config);
};
const removeDiscoverUserMembership = (userId, organizationId, config) => {
    const requestConfig = organizationId
        ? Object.assign(Object.assign({}, config), { headers: Object.assign(Object.assign({}, config === null || config === void 0 ? void 0 : config.headers), { "X-Peek-Organization": organizationId }) }) : config;
    return baseDiscoverService.delete(`api/users/${userId}/membership`, requestConfig);
};
// Grants additional org memberships to an existing (just-created or pre-existing) discover user.
// Task 8 open coordination point (work-stream D, backend owner Michael Paul Mead): the dedicated
// grant route for extra memberships wasn't confirmed in time, so per the plan's fallback (SDD
// ruling R4) this delegates to the same POST api/users the initial create uses — the backend
// identifies the target by `email` in the wire body and upserts the memberships onto the existing
// account. `userId` is accepted (unused) for call-site/API symmetry with
// `removeDiscoverUserMembership` and so this seam's signature doesn't have to change if/when the
// real grant route lands — only this function's body would.
//
// The upsert is conditional: the backend only upserts when at least one of the named organizations
// is MISSING from the account, and 409s `already_has_access` when every one of them is already
// held. Callers must therefore pass only genuinely new memberships — which is what sending them
// all in one request buys: there is no partial grant to resubmit, so no request ever re-names an
// organization that already landed.
const grantDiscoverUserMembership = (userId, payload, config) => {
    return createDiscoverUser(payload, config);
};
const resendDiscoverInvite = (userId, config) => {
    return baseDiscoverService.post(`api/users/${userId}/invite`, {}, config);
};
const requestDemoAccess = (payload, config) => {
    return baseDiscoverService.post("api/demo/access", payload, config);
};
const getTopCompetitors = (subjectId, assessmentId, config) => {
    const params = assessmentId ? { assessmentId } : undefined;
    return baseDiscoverService.get(`api/subjects/${subjectId}/top-competitors`, Object.assign(Object.assign({}, config), { params }));
};
const getMentionedCommunities = (subjectId, competitor, limit = 10, config) => {
    return baseDiscoverService.get(`api/subjects/${subjectId}/mentioned-communities`, Object.assign(Object.assign({}, config), { params: { competitor, limit } }));
};
const getAssessmentTemplateTrackedCompetitors = (subjectId, config) => {
    return baseDiscoverService.get(`api/subjects/${subjectId}/tracked-competitors`, config);
};
const replaceTrackedCompetitors = (subjectId, payload, config) => {
    return baseDiscoverService.put(`api/subjects/${subjectId}/tracked-competitors`, payload, config);
};
const getWhiskerDistribution = (subjectId, assessmentId, config) => {
    const params = assessmentId ? { assessmentId } : undefined;
    return baseDiscoverService.get(`api/subjects/${subjectId}/whisker-distribution`, Object.assign(Object.assign({}, config), { params }));
};
const getTrackedCompetitors = (subjectId, assessmentId, config) => {
    const params = assessmentId ? { assessmentId } : undefined;
    return baseDiscoverService.get(`api/subjects/${subjectId}/competitor-rankings`, Object.assign(Object.assign({}, config), { params }));
};
const signupAiDiscover = (payload, config) => {
    return baseDiscoverService.post("auth/signup", payload, config);
};
const createAssessmentTemplate = (payload, config) => {
    return baseDiscoverService.post("api/assessment-templates", payload, config);
};
const updateAssessmentTemplate = (templateId, payload, config) => {
    return baseDiscoverService.put(`api/assessment-templates/${templateId}`, payload, config);
};
const getAssessmentTemplates = (params, config) => {
    const _a = params !== null && params !== void 0 ? params : {}, { subjectIds } = _a, rest = __rest(_a, ["subjectIds"]);
    return baseDiscoverService.get("api/assessment-templates", Object.assign(Object.assign({}, config), { params: Object.assign(Object.assign({}, rest), ((subjectIds === null || subjectIds === void 0 ? void 0 : subjectIds.length) && { subjectIds: subjectIds.join(",") })) }));
};
const getAssessmentTemplate = (templateId, config) => {
    return baseDiscoverService.get(`api/assessment-templates/${templateId}`, config);
};
const toggleAssessmentTemplateStatus = (templateId, action, config) => {
    return baseDiscoverService.patch(`api/assessment-templates/${templateId}/${action}`, {}, config);
};
const forkAssessmentTemplateWithCollection = (templateId, payload, config) => {
    return baseDiscoverService.post(`api/assessment-templates/${templateId}/update-collection`, payload, config);
};
// ── Assessment Variable Collections ──
const VariableOverrideAction = {
    SET: "set",
    REMOVE: "remove",
};
const createAssessmentVariableCollection = (payload, config) => {
    return baseDiscoverService.post("api/assessment-variable-collections", payload, config);
};
const updateAssessmentVariableCollection = (collectionId, payload, config) => {
    return baseDiscoverService.put(`api/assessment-variable-collections/${collectionId}`, payload, config);
};
const seedBaseVariables = (collectionId, payload, config) => {
    return baseDiscoverService.post(`api/assessment-variable-collections/${collectionId}/seed-base-variables`, payload || {}, config);
};
const getAssessmentVariableCollection = (collectionId, config) => {
    return baseDiscoverService.get(`api/assessment-variable-collections/${collectionId}`, config);
};
const getAssessmentVariablesByAssessmentId = (assessmentId, config) => {
    return baseDiscoverService.get(`api/assessment-variables/${assessmentId}`, config);
};
// organizationBilling.ts on the BE keys every billing route by org in the PATH
// (/api/organizations/:organizationId/billing/...), including licenses.
const orgBilling = (organizationId) => `api/organizations/${organizationId}/billing`;
const getBilling = (organizationId, config) => {
    return baseDiscoverService.get(orgBilling(organizationId), config);
};
const getBillingSubscription = (organizationId, config) => {
    return baseDiscoverService.get(`${orgBilling(organizationId)}/subscription`, config);
};
const getBillingSubscriptions = (organizationId, config) => {
    return baseDiscoverService.get(`${orgBilling(organizationId)}/subscriptions`, config);
};
const getBillingPaymentMethods = (organizationId, config) => {
    return baseDiscoverService.get(`${orgBilling(organizationId)}/payment-methods`, config);
};
const getBillingInvoices = (organizationId, config) => {
    return baseDiscoverService.get(`${orgBilling(organizationId)}/invoices`, config);
};
const getBillingPlans = (organizationId, config) => {
    return baseDiscoverService.get(`${orgBilling(organizationId)}/plans`, config);
};
const createBillingSetupIntent = (organizationId, config) => {
    return baseDiscoverService.post(`${orgBilling(organizationId)}/setup-intent`, {}, config);
};
const createBillingSubscription = (organizationId, payload, config) => {
    return baseDiscoverService.post(`${orgBilling(organizationId)}/subscription`, payload, config);
};
// ── Free report (POST /api/organizations/:organizationId/billing/free-report) ──
// Claims the organization's one free Basic report: no body, no card. The server creates a local
// Basic license (quantity 1) and answers 201 with its subscription id. Refusals come back through
// responseFailCallbackDiscover as error.cause.code (HTTP status) + error.cause.errorCode.
const FREE_REPORT_ERROR_CODES = {
    // 403: the organization has no free report to claim (already used, or not eligible).
    UNAVAILABLE: "free_report_unavailable",
    // 403: the claimant's email domain is not a business domain.
    DOMAIN_BLOCKED: "free_report_domain_blocked",
};
const claimFreeReport = (organizationId, config) => {
    return baseDiscoverService.post(`${orgBilling(organizationId)}/free-report`, {}, config);
};
const deleteBillingPaymentMethod = (organizationId, paymentMethodId, config) => {
    return baseDiscoverService.delete(`${orgBilling(organizationId)}/payment-methods/${paymentMethodId}`, config);
};
// Prefer the deterministic id-based route; the no-id alias (single active sub,
// 409 if ambiguous) is the fallback when no subscriptionId is known.
const cancelBillingSubscription = (organizationId, subscriptionId, config) => {
    const path = subscriptionId
        ? `${orgBilling(organizationId)}/subscriptions/${subscriptionId}`
        : `${orgBilling(organizationId)}/subscription`;
    return baseDiscoverService.delete(path, config);
};
const reactivateBillingSubscription = (organizationId, subscriptionId, config) => {
    const path = subscriptionId
        ? `${orgBilling(organizationId)}/subscriptions/${subscriptionId}`
        : `${orgBilling(organizationId)}/subscription`;
    return baseDiscoverService.patch(path, {}, config);
};
const getBillingLicenses = (organizationId, config) => {
    return baseDiscoverService.get(`${orgBilling(organizationId)}/licenses`, config);
};
const assignBillingLicense = (subscriptionId, subjectId, organizationId, config) => {
    return baseDiscoverService.post(`${orgBilling(organizationId)}/licenses/${subscriptionId}/assign`, { subjectId }, config);
};
const unassignBillingLicense = (subscriptionId, subjectId, organizationId, config) => {
    return baseDiscoverService.post(`${orgBilling(organizationId)}/licenses/${subscriptionId}/unassign`, { subjectId }, config);
};
const markOrganizationAsEnterprise = (organizationId, config) => {
    return baseDiscoverService.put(`${orgBilling(organizationId)}/enterprise`, {}, config);
};
const getDiscoverSubjectRecommendationRuns = (subjectId, assessmentId, config) => {
    const params = assessmentId ? { assessmentId } : undefined;
    return baseDiscoverService.get(`api/subjects/${subjectId}/recommendation-runs`, Object.assign(Object.assign({}, config), { params }));
};
const postReportsExportBundle = (payload, config) => {
    return baseDiscoverService.post("api/reports/export/bundle", payload, config);
};
const getReportExport = (assessmentId, format, config) => {
    return baseDiscoverService.get(`api/reports/${assessmentId}/export/${format}`, config);
};
/** The API takes a comma-separated list; an empty list means "all", which is the param's absence. */
const toProvidersParam = (providers) => (providers === null || providers === void 0 ? void 0 : providers.length) ? providers.join(",") : undefined;
const getPortfolio = ({ providers, skip, limit } = {}, config) => {
    return baseDiscoverService.get("api/portfolio", Object.assign(Object.assign({}, config), { params: { providers: toProvidersParam(providers), skip, limit } }));
};
const getPortfolioTrend = ({ providers, startDate, endDate } = {}, config) => {
    return baseDiscoverService.get("api/portfolio/trend", Object.assign(Object.assign({}, config), { params: { providers: toProvidersParam(providers), startDate, endDate } }));
};
// Takes no community list and no model filter: the server builds the portfolio from the caller's
// own access, and the PDF always covers every model. `organizationId` names the organization the
// page is on, which the recommendations section is read for.
const getPortfolioExport = (organizationId, config) => {
    return baseDiscoverService.get("api/portfolio/export/pdf", Object.assign(Object.assign({}, config), { params: Object.assign(Object.assign({}, config === null || config === void 0 ? void 0 : config.params), (organizationId ? { organizationId } : {})) }));
};
// The CSV is built server-side: it carries the per-community detail the card's own response does
// not, which is why this is a round trip rather than a dump of the five visible rows.
const getOrganizationRecommendationCategoriesExport = (organizationId, config) => {
    return baseDiscoverService.get(`api/organizations/${organizationId}/recommendation-category/export`, config);
};
const getOrganizationRecommendationCategories = (organizationId, config) => {
    return baseDiscoverService.get(`api/organizations/${organizationId}/recommendation-category`, config);
};

export { BillingMode, FREE_REPORT_ERROR_CODES, RUNNING_ASSESSMENT_STATUSES, VariableOverrideAction, assignBillingLicense, cancelBillingSubscription, claimFreeReport, continueAssessment, createAssessmentTemplate, createAssessmentVariableCollection, createBillingSetupIntent, createBillingSubscription, createDiscoverOrganization, createDiscoverSubject, createDiscoverUser, createMarketProfileReport, createReport, deleteBillingPaymentMethod, deleteDiscoverOrganization, deleteDiscoverSubject, deleteDiscoverUser, forkAssessmentTemplateWithCollection, getAiDiscoverabilityReport, getAiDiscoverabilityReports, getAssessmentStatus, getAssessmentTemplate, getAssessmentTemplateTrackedCompetitors, getAssessmentTemplates, getAssessmentVariableCollection, getAssessmentVariablesByAssessmentId, getBilling, getBillingInvoices, getBillingLicenses, getBillingPaymentMethods, getBillingPlans, getBillingSubscription, getBillingSubscriptions, getDiscoverOrganizations, getDiscoverSubjectById, getDiscoverSubjectRecommendationRuns, getDiscoverSubjectTimeSeries, getDiscoverSubjectTopSources, getDiscoverSubjects, getDiscoverUsers, getMentionedCommunities, getOrganizationRecommendationCategories, getOrganizationRecommendationCategoriesExport, getPortfolio, getPortfolioExport, getPortfolioTrend, getReportExport, getTopCompetitors, getTrackedCompetitors, getWhiskerDistribution, grantDiscoverUserMembership, markOrganizationAsEnterprise, postReportsExportBundle, reactivateBillingSubscription, removeDiscoverUserMembership, replaceTrackedCompetitors, requestDemoAccess, resendDiscoverInvite, seedBaseVariables, signupAiDiscover, toggleAssessmentTemplateStatus, unassignBillingLicense, updateAssessmentTemplate, updateAssessmentVariableCollection, updateDiscoverOrganization, updateDiscoverSubject, updateDiscoverUser, updateDiscoverUserStatus };
